import React from 'react';
import { getScoreColor } from '../insightsTheme';
import RankedBars from './RankedBars';

/** * Displays yes-rate results for boolean questions using RankedBars.
 * Percentages are already on a 0–100 scale.
 *
 * Use AverageScoreBars for numeric questions.
 */

export interface YesRateBarRow {
    /** Project name or question topic shown beside the bar. */
    label: string;
    /** Yes rate from 0 to 100. */
    percentage: number;
    responses: number;
    /**
     * Yes answers behind the rate. Never shown on the row itself, only in the hover line,
     * because a rate hides its sample size: 100% from 3 answers gets the longest bar on the
     * chart, and the rows cannot otherwise be reconciled with the combined figure above.
     */
    yesCount: number;
    /** Full question text shown when the label is truncated. */
    questiontext?: string | null;
}

/** "1 yes of 3 responses", "7 yes of 18 responses". */
const describeRate = (yesCount: number, responses: number): string =>
    `${yesCount.toLocaleString()} yes of ${responses.toLocaleString()} ${responses === 1 ? 'response' : 'responses'}`;

interface YesRateBarsProps {
    rows: YesRateBarRow[];
    /** Optional fixed bar color. Otherwise, color is based on the yes rate. */
    barColor?: string;
    /** Optional fixed color for the value text. Otherwise, it follows the bar color. */
    valueColor?: string;
    /** Maximum number of highest-rated rows to display. */
    limit?: number;
}

const YesRateBars: React.FC<YesRateBarsProps> = ({ rows, barColor, valueColor, limit }) => (
    <RankedBars
        rows={rows}
        limit={limit}
        valueColor={valueColor}
        rank={(row) => row.percentage}
        toBar={(row) => ({
            label: row.label,
            questiontext: row.questiontext,
            percentage: row.percentage,
            displayValue: `${Math.round(row.percentage)}%`,
            tooltip: describeRate(row.yesCount, row.responses),
            // Set per row, so each bar can have its own colour based on its value.
            barColor: barColor ?? getScoreColor(row.percentage),
        })}
    />
);

export default YesRateBars;
